import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { 
  Users, 
  Clock, 
  Coffee, 
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Eye,
  MapPin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPointage = () => {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    fetchDashboard();
    // Rafraîchir toutes les minutes
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/pointage/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
        setError(null);
      } else {
        setError('Erreur lors du chargement des données');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'present':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'en_pause':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'parti':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'absent':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'present':
        return <CheckCircle className="w-4 h-4" />;
      case 'en_pause':
        return <Coffee className="w-4 h-4" />;
      case 'parti':
        return <XCircle className="w-4 h-4" />;
      case 'absent':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'present':
        return 'Présent';
      case 'en_pause':
        return 'En pause';
      case 'parti':
        return 'Parti';
      case 'absent':
        return 'Absent';
      default:
        return 'Inconnu';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const calculateWorkHours = (entree, sortie, pauseDebut, pauseFin) => {
    if (!entree) return '0h00';
    
    const start = new Date(entree);
    const end = sortie ? new Date(sortie) : new Date();
    
    let workTime = end - start;
    
    // Soustraire le temps de pause
    if (pauseDebut && pauseFin) {
      const pauseStart = new Date(pauseDebut);
      const pauseEnd = new Date(pauseFin);
      workTime -= (pauseEnd - pauseStart);
    } else if (pauseDebut && !pauseFin) {
      // En pause actuellement
      const pauseStart = new Date(pauseDebut);
      workTime -= (new Date() - pauseStart);
    }
    
    const hours = Math.floor(workTime / (1000 * 60 * 60));
    const minutes = Math.floor((workTime % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h${minutes.toString().padStart(2, '0')}`;
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès non autorisé</h2>
        <p className="text-gray-600">Seuls les administrateurs peuvent accéder à cette page.</p>
      </div>
    );
  }

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mr-3" />
        <span className="text-lg text-gray-600">Chargement des données...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Pointages</h1>
          <p className="text-gray-600">Suivi en temps réel de la présence des employés</p>
        </div>
        
        <div className="flex items-center gap-4">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={fetchDashboard}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {dashboard && (
        <>
          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Employés</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboard.total_employes}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Présents</p>
                  <p className="text-2xl font-bold text-green-600">{dashboard.presents}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Coffee className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">En Pause</p>
                  <p className="text-2xl font-bold text-orange-600">{dashboard.en_pause}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Absents</p>
                  <p className="text-2xl font-bold text-red-600">{dashboard.absents}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Partis</p>
                  <p className="text-2xl font-bold text-gray-600">{dashboard.partis}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tableau des employés */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Présence des Employés</h2>
              <p className="text-sm text-gray-600">
                Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Employé
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Arrivée
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Pause
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Départ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Temps de travail
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {dashboard.employes.map((employe) => (
                    <tr key={employe.user_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              {employe.prenom.charAt(0)}{employe.nom.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {employe.prenom} {employe.nom}
                            </p>
                            <p className="text-sm text-gray-500 capitalize">
                              {employe.role.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(employe.status)}`}>
                          {getStatusIcon(employe.status)}
                          {getStatusLabel(employe.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatTime(employe.entree)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {employe.pause_debut && (
                          <div>
                            <div>{formatTime(employe.pause_debut)}</div>
                            {employe.pause_fin && (
                              <div className="text-xs text-gray-500">
                                → {formatTime(employe.pause_fin)}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatTime(employe.sortie)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {calculateWorkHours(employe.entree, employe.sortie, employe.pause_debut, employe.pause_fin)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedEmployee(employe)}
                          className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Eye className="w-3 h-3" />
                          Détails
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {dashboard.employes.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucun employé trouvé</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal de détails employé */}
      {selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedEmployee.prenom} {selectedEmployee.nom}
                  </h3>
                  <p className="text-sm text-gray-600 capitalize">
                    {selectedEmployee.role.replace('_', ' ')}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <XCircle className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Statut actuel */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Statut Actuel</h4>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedEmployee.status)}`}>
                    {getStatusIcon(selectedEmployee.status)}
                    {getStatusLabel(selectedEmployee.status)}
                  </span>
                </div>
              </div>

              {/* Historique des pointages */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Pointages d'Aujourd'hui</h4>
                {selectedEmployee.pointages.length > 0 ? (
                  <div className="space-y-2">
                    {[...selectedEmployee.pointages]
                      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                      .map((pointage) => (
                      <div key={`${pointage.timestamp}-${pointage.type}`} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            {pointage.type === 'entree' && <CheckCircle className="w-4 h-4 text-green-600" />}
                            {pointage.type === 'sortie' && <XCircle className="w-4 h-4 text-red-600" />}
                            {pointage.type === 'pause_debut' && <Coffee className="w-4 h-4 text-orange-600" />}
                            {pointage.type === 'pause_fin' && <CheckCircle className="w-4 h-4 text-blue-600" />}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 capitalize">
                              {pointage.type.replace('_', ' ')}
                            </p>
                            <p className="text-sm text-gray-600">
                              {formatTime(pointage.timestamp)}
                            </p>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTime(pointage.timestamp)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Aucun pointage aujourd'hui</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPointage;